import { isNativeApp } from './platform'
import { BRAND_LOGO_URL } from './brand-logo'
import type { CallMediaType } from '../types/voice-call'
import {
  getRingtonePreference,
  resolveRingtonePlaybackUrl,
} from './ringtone-preference'
import { getCachedOrgDialToneConfig } from '../features/dial-tones/dial-tone-org-service'

/**
 * Background-tab incoming-call alerting for the WEB (PWA) build only.
 *
 * When an incoming call arrives while the tab is hidden, browsers won't show
 * the in-app overlay where the user can see it and tend to suspend/throttle
 * audio + rAF in background tabs. This module layers three best-effort web
 * alerts on top of the in-app overlay/ringtone:
 *   1. A system Notification ("Incoming call from {name}") that focuses the tab
 *      on click. Shown via the service worker registration when available
 *      (more reliable in background), else the page-level Notification.
 *   2. A document-title flash ("📞 Incoming call…") for a glance at the tab.
 *   3. A dedicated, non-faded background ringtone element that does not rely on
 *      requestAnimationFrame (which is paused in hidden tabs), giving the best
 *      chance of an audible ring while backgrounded.
 *
 * Visible-but-unfocused tabs rely on the in-app ringtone hook only — a second
 * HTMLAudioElement here previously stacked dial + ringtone (or double ring).
 *
 * Native (Capacitor) builds use FCM push + a local notification instead and are
 * intentionally excluded from everything here.
 */

const TITLE_FLASH_INTERVAL_MS = 1000
const NOTIFICATION_TAG_PREFIX = 'webcall-'
const BACKGROUND_RING_VOLUME = 0.85

let permissionPrimeArmed = false

function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

/** Current web Notification permission, or 'unsupported'. */
export function getWebNotificationPermission(): NotificationPermission | 'unsupported' {
  if (isNativeApp() || !notificationsSupported()) return 'unsupported'
  return Notification.permission
}

/** Request web Notification permission once. Safe to call repeatedly. */
export async function requestWebNotificationPermission(): Promise<
  NotificationPermission | 'unsupported'
> {
  if (isNativeApp() || !notificationsSupported()) return 'unsupported'
  if (Notification.permission !== 'default') return Notification.permission
  try {
    return await Notification.requestPermission()
  } catch {
    return Notification.permission
  }
}

/**
 * Arm a one-time user-gesture listener that requests Notification permission.
 * Browsers (notably Chrome) require a gesture to surface the prompt, and a call
 * may arrive while the tab is hidden — so we ask shortly after sign-in once the
 * user interacts, rather than at the moment of the first call.
 */
export function primeWebNotificationPermission(): void {
  if (isNativeApp() || !notificationsSupported()) return
  if (permissionPrimeArmed) return
  if (Notification.permission !== 'default') return
  permissionPrimeArmed = true

  const ask = () => {
    window.removeEventListener('pointerdown', ask)
    window.removeEventListener('keydown', ask)
    window.removeEventListener('touchstart', ask)
    void requestWebNotificationPermission()
  }

  window.addEventListener('pointerdown', ask, { once: true, passive: true })
  window.addEventListener('touchstart', ask, { once: true, passive: true })
  window.addEventListener('keydown', ask, { once: true })
}

// ---------------------------------------------------------------------------
// System notification
// ---------------------------------------------------------------------------

let pageNotification: Notification | null = null

function notificationTag(callId: string): string {
  return `${NOTIFICATION_TAG_PREFIX}${callId}`
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null
  } catch {
    return null
  }
}

async function showCallNotification(input: {
  callId: string
  callerName: string
  mediaType: CallMediaType
}): Promise<void> {
  if (getWebNotificationPermission() !== 'granted') return

  const title = `Incoming ${input.mediaType === 'video' ? 'video ' : ''}call`
  const options: NotificationOptions & { renotify?: boolean; vibrate?: number[] } = {
    body: `${input.callerName} is calling you`,
    icon: BRAND_LOGO_URL,
    badge: BRAND_LOGO_URL,
    tag: notificationTag(input.callId),
    renotify: true,
    requireInteraction: true,
    silent: false,
    vibrate: [400, 200, 400, 200, 400],
    data: { callId: input.callId, type: 'incoming-call' },
  }

  // Prefer the service worker registration — it can show/persist notifications
  // more reliably when the page is backgrounded, and clicks are routed through
  // the SW's notificationclick handler to focus the tab.
  const registration = await getServiceWorkerRegistration()
  if (registration?.showNotification) {
    try {
      await registration.showNotification(title, options)
      return
    } catch {
      /* fall through to page-level Notification */
    }
  }

  try {
    closePageNotification()
    const notification = new Notification(title, options)
    notification.onclick = () => {
      try {
        window.focus()
      } catch {
        /* ignore */
      }
      notification.close()
    }
    pageNotification = notification
  } catch {
    /* construction not allowed (e.g. mobile Chrome) and no SW available */
  }
}

function closePageNotification(): void {
  if (pageNotification) {
    try {
      pageNotification.close()
    } catch {
      /* ignore */
    }
    pageNotification = null
  }
}

async function clearCallNotification(callId: string): Promise<void> {
  closePageNotification()
  const registration = await getServiceWorkerRegistration()
  if (!registration?.getNotifications) return
  try {
    const notifications = await registration.getNotifications({ tag: notificationTag(callId) })
    for (const notification of notifications) notification.close()
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Document-title flash
// ---------------------------------------------------------------------------

let titleFlashTimer: ReturnType<typeof setInterval> | null = null
let savedTitle: string | null = null

function startTitleFlash(callerName: string): void {
  if (typeof document === 'undefined') return
  if (titleFlashTimer) return
  savedTitle = document.title
  let on = false
  const apply = () => {
    on = !on
    document.title = on ? '📞 Incoming call…' : `📞 ${callerName} is calling`
  }
  apply()
  titleFlashTimer = setInterval(apply, TITLE_FLASH_INTERVAL_MS)
}

function stopTitleFlash(): void {
  if (titleFlashTimer) {
    clearInterval(titleFlashTimer)
    titleFlashTimer = null
  }
  if (savedTitle !== null && typeof document !== 'undefined') {
    document.title = savedTitle
  }
  savedTitle = null
}

// ---------------------------------------------------------------------------
// Background ringtone (no rAF — survives hidden-tab throttling)
// ---------------------------------------------------------------------------

let backgroundRing: HTMLAudioElement | null = null

function startBackgroundRing(): void {
  if (typeof window === 'undefined') return
  if (!backgroundRing) {
    const el = new Audio()
    el.loop = true
    el.preload = 'auto'
    el.setAttribute('playsinline', 'true')
    backgroundRing = el
  }
  const catalog = getCachedOrgDialToneConfig()?.ringtoneCatalog.slots
  const url = resolveRingtonePlaybackUrl(getRingtonePreference(), catalog)
  if (backgroundRing.src !== url) {
    backgroundRing.src = url
    backgroundRing.load()
  }
  // Fixed volume (no fade) so we don't depend on requestAnimationFrame, which
  // browsers pause in hidden tabs.
  backgroundRing.volume = BACKGROUND_RING_VOLUME
  void backgroundRing.play().catch(() => {
    /* autoplay blocked — system notification sound is the fallback */
  })
}

function stopBackgroundRing(): void {
  if (!backgroundRing) return
  try {
    backgroundRing.pause()
    backgroundRing.currentTime = 0
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Incoming-call alert orchestration
// ---------------------------------------------------------------------------

type AlertHandle = {
  callId: string
  teardown: () => void
}

let activeAlert: AlertHandle | null = null

/** True when the tab cannot show the in-app overlay to the user right now. */
function isBackgrounded(): boolean {
  if (typeof document === 'undefined') return false
  // Only treat fully hidden tabs as backgrounded. Visible-but-unfocused
  // (DevTools, another pane) still runs the in-app ringtone — stacking a
  // second HTMLAudioElement caused dial + ringtone (or double ring) on web.
  return document.visibilityState === 'hidden'
}

/**
 * Begin web background-tab alerting for an incoming call. Idempotent per call;
 * starting a new call ends the previous alert. No-op on native builds.
 */
export function beginIncomingCallAlert(input: {
  callId: string
  callerName: string
  mediaType: CallMediaType
}): void {
  if (isNativeApp() || typeof window === 'undefined') return
  if (activeAlert?.callId === input.callId) return
  if (activeAlert) activeAlert.teardown()

  const refresh = () => {
    if (isBackgrounded()) {
      void showCallNotification(input)
      startTitleFlash(input.callerName)
      startBackgroundRing()
    } else {
      // User is looking at the tab — the in-app overlay + faded ringtone take
      // over, so clear the background-only affordances.
      void clearCallNotification(input.callId)
      stopTitleFlash()
      stopBackgroundRing()
    }
  }

  document.addEventListener('visibilitychange', refresh)
  window.addEventListener('focus', refresh)
  window.addEventListener('blur', refresh)
  refresh()

  activeAlert = {
    callId: input.callId,
    teardown: () => {
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('focus', refresh)
      window.removeEventListener('blur', refresh)
      stopTitleFlash()
      stopBackgroundRing()
      void clearCallNotification(input.callId)
    },
  }
}

/** End web alerting for a call (answered / declined / cancelled / timed out). */
export function endIncomingCallAlert(callId?: string): void {
  if (!activeAlert) return
  if (callId && activeAlert.callId !== callId) return
  activeAlert.teardown()
  activeAlert = null
}
